<template>
  <div>
    <Header />

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Employees Directory</h5>
        <p class="page-sub">View and manage all employees in your organisation</p>
      </div>
      <div class="page-actions">

        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input type="text" class="search-input" placeholder="Search employees..."
            v-model="tableData.search" @input="debouncedFetch" />
        </div>

        <select v-model="tableData.status" @change="fetchEmployees()" class="filter-select">
          <option :value="[0,1]">All Statuses</option>
          <option :value="[1]">Active</option>
          <option :value="[0]">Inactive</option>
        </select>

        <!-- Filter toggle -->
        <button class="btn-filter" :class="{ 'btn-filter--active': filtersOpen || activeFilterCount > 0 }"
          @click="filtersOpen = !filtersOpen">
          <font-awesome-icon icon="filter" />
          Filter
          <span v-if="activeFilterCount > 0" class="filter-count">{{ activeFilterCount }}</span>
        </button>

        <!-- Export -->
        <div class="dropdown-wrap" v-click-outside="() => exportOpen = false">
          <button class="btn-export" @click="exportOpen = !exportOpen" :disabled="exportLoading">
            <font-awesome-icon :icon="exportLoading ? 'spinner' : 'download'" :spin="exportLoading" />
            Export
            <font-awesome-icon icon="chevron-down" class="chevron" />
          </button>
          <div v-if="exportOpen" class="action-dropdown">
            <button class="action-dropdown-item" @click="openExportModal('csv')">
              <div class="action-dropdown-icon" style="background:rgba(22,163,74,.1);color:#16a34a;"><font-awesome-icon icon="file-csv" /></div>
              <div>
                <div class="action-dropdown-label">Export CSV</div>
                <div class="action-dropdown-hint">Spreadsheet-compatible format</div>
              </div>
            </button>
            <button class="action-dropdown-item" @click="openExportModal('pdf')">
              <div class="action-dropdown-icon" style="background:rgba(220,38,38,.1);color:#dc2626;"><font-awesome-icon icon="file-pdf" /></div>
              <div>
                <div class="action-dropdown-label">Export PDF</div>
                <div class="action-dropdown-hint">Printable employee list</div>
              </div>
            </button>
          </div>
        </div>

        <button class="btn-danger-outline"
          :disabled="permission.delete_employee != 1"
          data-bs-toggle="modal" data-bs-target="#bulkModal">
          <font-awesome-icon icon="trash" />
          Bulk Delete
          <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
        </button>

        <!-- New Employee dropdown -->
        <div class="dropdown-wrap" v-click-outside="closeDropdown">
          <button class="btn-primary-action" @click="dropdownOpen = !dropdownOpen">
            <font-awesome-icon icon="plus" />
            New Employee
            <font-awesome-icon icon="chevron-down" class="chevron" />
          </button>
          <div v-if="dropdownOpen" class="action-dropdown">
            <button class="action-dropdown-item" @click="handleNew('single')">
              <div class="action-dropdown-icon"><font-awesome-icon icon="user" /></div>
              <div>
                <div class="action-dropdown-label">Single Employee</div>
                <div class="action-dropdown-hint">Add one employee manually</div>
              </div>
            </button>
            <button class="action-dropdown-item" @click="handleNew('invite')">
              <div class="action-dropdown-icon"><font-awesome-icon icon="envelope" /></div>
              <div>
                <div class="action-dropdown-label">Invite Employee</div>
                <div class="action-dropdown-hint">Send invitation email</div>
              </div>
            </button>
            <button class="action-dropdown-item" @click="handleNew('bulk_invite')">
              <div class="action-dropdown-icon"><font-awesome-icon icon="envelope" /></div>
              <div>
                <div class="action-dropdown-label">Bulk Invite</div>
                <div class="action-dropdown-hint">Send invitations to multiple employees</div>
              </div>
            </button>
            <button class="action-dropdown-item" @click="handleNew('upload')">
              <div class="action-dropdown-icon"><font-awesome-icon icon="upload" /></div>
              <div>
                <div class="action-dropdown-label">Upload Employees</div>
                <div class="action-dropdown-hint">Import from CSV / Excel</div>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- ── Filter Panel ── -->
    <transition name="filter-slide">
      <div v-if="filtersOpen" class="filter-panel">
        <div class="filter-panel-grid">
          <div class="filter-field">
            <label class="filter-label">Location</label>
            <select v-model="tableData.location_id" @change="fetchEmployees()" class="filter-select filter-select--full">
              <option value="">All Locations</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
          </div>
          <div class="filter-field">
            <label class="filter-label">Department</label>
            <select v-model="tableData.department_id" @change="fetchEmployees()" class="filter-select filter-select--full">
              <option value="">All Departments</option>
              <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
            </select>
          </div>
          <div class="filter-field">
            <label class="filter-label">Unit</label>
            <select v-model="tableData.unit_id" @change="fetchEmployees()" class="filter-select filter-select--full">
              <option value="">All Units</option>
              <option v-for="unit in units" :key="unit.id" :value="unit.id">{{ unit.name }}</option>
            </select>
          </div>
          <div class="filter-field">
            <label class="filter-label">Position</label>
            <select v-model="tableData.position_id" @change="fetchEmployees()" class="filter-select filter-select--full">
              <option value="">All Positions</option>
              <option v-for="pos in positions" :key="pos.id" :value="pos.id">{{ pos.name }}</option>
            </select>
          </div>
        </div>
        <div class="filter-panel-footer" v-if="activeFilterCount > 0">
          <button class="btn-clear-filters" @click="clearFilters()">
            <font-awesome-icon icon="times" />
            Clear {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }}
          </button>
        </div>
      </div>
    </transition>

    <!-- ── Stat Cards ── -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
          <font-awesome-icon icon="users" />
        </div>
        <div>
          <div class="stat-value">{{ pagination ? pagination.total : employees.length }}</div>
          <div class="stat-label">Total Employees</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background:rgba(22,163,74,.1);color:#16a34a;">
          <font-awesome-icon icon="building" />
        </div>
        <div>
          <div class="stat-value">{{ totalDepartments }}</div>
          <div class="stat-label">Departments</div>
        </div>
      </div>
    </div>

    <!-- ── Data Card ── -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading employees...</p>
      </div>

      <!-- 403 -->
      <div v-else-if="permissionStatus === 403" class="state-block">
        <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
        <p class="state-title">Access Denied</p>
        <p class="state-sub">You don't have permission to view the employees directory.</p>
      </div>

      <!-- Empty -->
      <div v-else-if="employees.length === 0" class="state-block">
        <div class="state-icon state-icon--blue"><font-awesome-icon icon="users" /></div>
        <p class="state-title">No employees found</p>
        <p class="state-sub">
          {{ tableData.search ? 'No results match your search.' : 'Employees will appear here once added.' }}
        </p>
      </div>

      <!-- Table -->
      <template v-else>
        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            <strong>{{ employees.length }}</strong> employee{{ employees.length !== 1 ? 's' : '' }}
            <span v-if="selectedModel.length > 0" class="selected-hint">
              &middot; {{ selectedModel.length }} selected
            </span>
          </span>
          <div class="per-page-wrap">
            <label class="per-page-label">Show</label>
            <select class="per-page-select" v-model.number="tableData.length" @change="tableData.page = 1; fetchEmployees()">
              <option v-for="n in [5, 10, 20, 50, 100, 200, 500, 1000]" :key="n" :value="n">{{ n }}</option>
            </select>
            <span class="per-page-label">per page</span>
          </div>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-check">
                  <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                </th>
                <th>Employee ID</th>
                <th>Employee</th>
                <th>Birthday</th>
                <th>Phone</th>
                <th>Department</th>
                <th>Paygrade</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="emp in employees" :key="emp.id" :class="{ 'row--selected row-clickable': selectedModel.includes(emp.id) }"
                @click="$router.push('/employees/' + emp.id)">

                <td class="col-check">
                  <input type="checkbox" class="row-check" v-model="selectedModel" :value="emp.id"
                    :disabled="emp.is_super" :title="emp.is_super ? 'Business owner cannot be deleted' : ''" />
                </td>
                <td class="text-muted-cell">{{ emp.employee_no }}</td>
                <!-- Avatar + name -->
                <td>
                  <div class="emp-cell">
                    <img v-if="emp.avatar_url" :src="emp.avatar_url" class="emp-avatar emp-avatar--img" alt="" />
                    <div v-else class="emp-avatar">{{ getInitials(emp.first_name, emp.last_name) }}</div>
                    <div>
                      <div class="emp-name">
                        {{ [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '—' }}
                      </div>
                      <span v-if="emp.is_super" class="owner-badge">
                        <font-awesome-icon icon="star" class="owner-badge-icon" />
                        Business Owner
                      </span>
                      <div class="emp-email">{{ emp.email || '—' }}</div>
                    </div>
                  </div>
                </td>

                <td class="text-muted-cell">{{ formatBirthday(emp.dob) }}</td>
                <td class="text-muted-cell">{{ emp.phone_number }}</td>
                <td>
                  <span v-if="emp.department" class="dept-badge">
                    {{ emp.department.name }}
                  </span>
                  <span v-else class="text-muted-cell">—</span>
                </td>

                <td>
                  <span v-if="emp.paygrade" class="dept-badge">
                    {{ emp.paygrade.name }}
                  </span>
                  <span v-else class="text-muted-cell">—</span>
                </td>

                <td class="col-actions" @click.stop>
                  <button class="action-icon-btn"
                    title="View details"
                    @click="$router.push('/employees/' + emp.id)">
                    <font-awesome-icon icon="chevron-right" />
                  </button>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.last_page > 1" class="pagination-bar">
          <span class="pagination-info">
            Showing {{ (pagination.current_page - 1) * tableData.length + 1 }}–{{ Math.min(pagination.current_page * tableData.length, pagination.total) }} of {{ pagination.total }}
          </span>
          <div class="pagination-controls">
            <button class="pg-btn" :disabled="pagination.current_page <= 1"
              @click="tableData.page = 1; fetchEmployees()">«</button>
            <button class="pg-btn" :disabled="pagination.current_page <= 1"
              @click="tableData.page = pagination.current_page - 1; fetchEmployees()">‹ Prev</button>
            <span class="pg-pages">
              <button
                v-for="p in pageRange"
                :key="p"
                class="pg-num"
                :class="{ 'pg-num--active': p === pagination.current_page }"
                @click="tableData.page = p; fetchEmployees()"
              >{{ p }}</button>
            </span>
            <button class="pg-btn" :disabled="pagination.current_page >= pagination.last_page"
              @click="tableData.page = pagination.current_page + 1; fetchEmployees()">Next ›</button>
            <button class="pg-btn" :disabled="pagination.current_page >= pagination.last_page"
              @click="tableData.page = pagination.last_page; fetchEmployees()">»</button>
          </div>
        </div>
      </template>
    </div>


    <!-- ══════════════════════════════
         INVITE EMPLOYEE MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: inviteOpen }" @click.self="inviteOpen = false">
      <div class="modal-box">
        <div class="modal-hd">
          <div class="modal-hd-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
            <font-awesome-icon icon="envelope" />
          </div>
          <div>
            <span class="modal-ttl">Invite Employee</span>
            <p class="modal-sub">Send an invitation email</p>
          </div>
          <button class="modal-close-btn" @click="inviteOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <form @submit.prevent="submitInvite">
          <div class="modal-bd">
            <div class="field-group">
              <label class="field-label">First Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="inviteForm.first_name" required />
            </div>
            <div class="field-group">
              <label class="field-label">Last Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="inviteForm.last_name" required />
            </div>
            <div class="field-group">
              <label class="field-label">Official Email Address <span class="req">*</span></label>
              <input type="email" class="field-input" v-model="inviteForm.email" required />
            </div>
            <div class="field-group">
              <label class="field-label">Personal Email Address </label>
              <input type="email" class="field-input" v-model="inviteForm.personal_email" />
            </div>
          </div>
          <div class="modal-ft">
            <button type="button" class="btn-ghost" @click="inviteOpen = false">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="paper-plane" class="me-1" />
              {{ crudLoading ? 'Sending...' : 'Send Invitation' }}
            </button>
          </div>
        </form>
      </div>
    </div>


    <!-- BULK INVITE MODAL -->
    <div class="modal-overlay" :class="{ open: bulkInviteOpen }" @click.self="bulkInviteOpen = false">
      <div class="modal-box">
        <div class="modal-hd">
          <div class="modal-hd-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
            <font-awesome-icon icon="envelope" />
          </div>
          <div>
            <span class="modal-ttl">Bulk Invite</span>
            <p class="modal-sub">Send invitations to multiple employees via CSV</p>
          </div>
          <button class="modal-close-btn" @click="bulkInviteOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="modal-bd">
          <div class="upload-zone" :class="{ 'upload-zone--active': bulkFile }"
            @click="$refs.bulkFileInput.click()"
            @dragover.prevent
            @drop.prevent="onBulkFileDrop">
            <font-awesome-icon icon="file-csv" class="upload-icon" />
            <p class="upload-label">
              {{ bulkFile ? bulkFile.name : 'Click or drag a CSV file here' }}
            </p>
            <p class="upload-hint">Accepted: .csv, .xlsx</p>
            <input ref="bulkFileInput" type="file" accept=".csv,.xlsx" style="display:none"
              @change="bulkFile = $event.target.files[0]" />
          </div>

          <!-- Template download -->
          <div class="template-hint">
            <font-awesome-icon icon="info-circle" class="template-hint-icon" />
            <span>Not sure about the format?</span>
            <button type="button" class="btn-download-template" @click="downloadTemplate">
              <font-awesome-icon icon="download" class="me-1" />
              Download template
            </button>
          </div>

          <!-- Column reference -->
          <div class="template-cols">
            <p class="template-cols-title">Required & optional columns:</p>
            <div class="template-col-list">
              <span class="col-tag col-tag--required">first_name <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">last_name <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">official_email <span class="col-req">*</span></span>
              <span class="col-tag col-tag--optional">personal_email</span>
            </div>
            <p class="template-cols-note"><span class="col-req">*</span> required columns</p>
          </div>
        </div>
        <div class="modal-ft">
          <button type="button" class="btn-ghost" @click="bulkInviteOpen = false">Cancel</button>
          <button class="btn-primary-action" :disabled="!bulkFile || crudLoading" @click="submitBulkInvite">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="paper-plane" class="me-1" />
            {{ crudLoading ? 'Sending...' : 'Send Bulk Invites' }}
          </button>
        </div>
      </div>
    </div>


    <!-- ══════════════════════════════
         UPLOAD EMPLOYEES MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: uploadOpen }" @click.self="uploadOpen = false">
      <div class="modal-box">
        <div class="modal-hd">
          <div class="modal-hd-icon" style="background:rgba(22,163,74,.1);color:#16a34a;">
            <font-awesome-icon icon="upload" />
          </div>
          <div>
            <span class="modal-ttl">Upload Employees</span>
            <p class="modal-sub">Import employees from CSV or Excel</p>
          </div>
          <button class="modal-close-btn" @click="uploadOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="modal-bd">
          <div class="upload-zone" :class="{ 'upload-zone--active': uploadFile }"
            @click="$refs.uploadFileInput.click()"
            @dragover.prevent
            @drop.prevent="onUploadFileDrop">
            <font-awesome-icon icon="file-upload" class="upload-icon" />
            <p class="upload-label">
              {{ uploadFile ? uploadFile.name : 'Click or drag a CSV / Excel file here' }}
            </p>
            <p class="upload-hint">Supported formats: .csv, .xlsx</p>
            <input ref="uploadFileInput" type="file" accept=".csv,.xlsx" style="display:none"
              @change="uploadFile = $event.target.files[0]" />
          </div>

          <!-- Template download -->
          <div class="template-hint">
            <font-awesome-icon icon="info-circle" class="template-hint-icon" />
            <span>Not sure about the format?</span>
            <button type="button" class="btn-download-template" @click="downloadUploadTemplate">
              <font-awesome-icon icon="download" class="me-1" />
              Download template
            </button>
          </div>

          <!-- Column reference -->
          <div class="template-cols">
            <p class="template-cols-title">Required & optional columns:</p>
            <div class="template-col-list">
              <span class="col-tag col-tag--required">first_name <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">last_name <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">email <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">phone_number <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">hire_date <span class="col-req">*</span></span>
              <span class="col-tag col-tag--optional">middle_name</span>
              <span class="col-tag col-tag--optional">dob</span>
              <span class="col-tag col-tag--optional">gender</span>
              <span class="col-tag col-tag--optional">address</span>
              <span class="col-tag col-tag--optional">post_code</span>
              <span class="col-tag col-tag--optional">department</span>
              <span class="col-tag col-tag--optional">position</span>
            </div>
            <p class="template-cols-note"><span class="col-req">*</span> required columns</p>
          </div>

          <div class="field-group" style="margin-top:14px;">
            <label class="field-label">Department (optional)</label>
            <select class="field-input field-select" v-model="uploadDepartment">
              <option value="">— Assign to department —</option>
              <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                {{ dept.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-ft">
          <button type="button" class="btn-ghost" @click="uploadOpen = false">Cancel</button>
          <button class="btn-primary-action" :disabled="!uploadFile || crudLoading" @click="submitUpload">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="upload" class="me-1" />
            {{ crudLoading ? 'Uploading...' : 'Upload Employees' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="inviteOpen || bulkInviteOpen || uploadOpen" class="modal-backdrop-custom" />

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-action"
              :disabled="selectedExportCols.length === 0 || exportLoading"
              @click="doExport()">
              <font-awesome-icon
                :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'"
                :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>


<script>
import Header from './component/header.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'EmployeesDirectory',

  components: { Header },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },

  data() {
    return {
      employees:       [],
      departments:     [],
      units:           [],
      positions:       [],
      locations:       [],
      loading:         false,
      exportLoading:   false,
      crudLoading:     false,
      permissionStatus: null,
      dropdownOpen:    false,
      selectedModel: [],
      allSelected: false,
      statuses: [],
      pagination:  null,
      filtersOpen: false,
      exportOpen:  false,
      showExportModal:    false,
      pendingExportFmt:   'csv',
      selectedExportCols: [],
      exportColumnDefs: [
        { key: 'employee_no', label: 'Employee No', pdfWidth: 65 },
        { key: 'name',        label: 'Name',         pdfWidth: 100 },
        { key: 'email',       label: 'Email',        pdfWidth: 130 },
        { key: 'phone',       label: 'Phone',        pdfWidth: 80 },
        { key: 'department',  label: 'Department',   pdfWidth: 80 },
        { key: 'unit',        label: 'Unit',         pdfWidth: 70 },
        { key: 'position',    label: 'Position',     pdfWidth: 80 },
        { key: 'paygrade',    label: 'Paygrade',     pdfWidth: 70 },
        { key: 'location',    label: 'Location',     pdfWidth: 80 },
        { key: 'hire_date',   label: 'Hire Date',    pdfWidth: 70 },
        { key: 'status',      label: 'Status',       pdfWidth: 55 },
      ],

      tableData: {
        page: 1,
        search: '',
        length: 5,
        status: [0, 1],
        department_id: '',
        unit_id:       '',
        position_id:   '',
        location_id:   '',
      },

      inviteOpen: false,
      inviteForm: { first_name: '', last_name: '', email: '', personal_email: '' },

      bulkInviteOpen: false,
      bulkFile:       null,

      uploadOpen:       false,
      uploadFile:       null,
      uploadDepartment: '',

      toast: useToast(),
      store: useStore(),
    };
  },

  computed: {
    permission() {
      return this.store.state.getCurrentUserPermission;
    },

    totalDepartments() {
      const depts = new Set(
        this.employees
          .map(e => e.department)
          .filter(d => d && d !== '--'),
      );
      return depts.size;
    },

    activeFilterCount() {
      return ['department_id','unit_id','position_id','location_id']
        .filter(k => this.tableData[k]).length;
    },

    pageRange() {
      if (!this.pagination) return [];
      const cur   = this.pagination.current_page;
      const last  = this.pagination.last_page;
      const delta = 2;
      const range = [];
      for (let i = Math.max(1, cur - delta); i <= Math.min(last, cur + delta); i++) {
        range.push(i);
      }
      return range;
    },
  },

  created() {
    this.fetchEmployees();
    this.fetchLookups();
  },

  methods: {
    fetchEmployees() {
      this.loading = true;
      axios.get('/payroll/employees', { params: this.tableData })
        .then(({ data }) => {
          const payload = data.data;
          if (Array.isArray(payload)) {
            this.employees = payload;
            this.pagination = null;
          } else {
            this.employees  = Array.isArray(payload?.data) ? payload.data : [];
            this.pagination = payload;
          }

          this.statuses = this.employees.map(d => d.is_active);
          const ids = new Set(this.employees.map(d => d.id));
          this.selectedModel = this.selectedModel.filter(id => ids.has(id));
          if (!this.selectedModel.length) this.allSelected = false;
        })
        .catch(err => {
          this.permissionStatus = err?.response?.status ?? null;
          if (err?.response?.status !== 403) {
            this.toast.error(err.response?.data?.message ?? 'Could not load employees.');
          }
        })
        .finally(() => { this.loading = false; });
    },

    fetchLookups() {
      const norm = (data) => Array.isArray(data?.data?.data) ? data.data.data : Array.isArray(data?.data) ? data.data : [];
      Promise.allSettled([
        axios.get('/payroll/department/active'),
        axios.get('/payroll/unit/active'),
        axios.get('/payroll/position/active'),
        axios.get('/admin/location/active'),
      ]).then(([depts, units, positions, locs]) => {
        if (depts.status      === 'fulfilled') this.departments = norm(depts.value.data);
        if (units.status      === 'fulfilled') this.units       = norm(units.value.data);
        if (positions.status  === 'fulfilled') this.positions   = norm(positions.value.data);
        if (locs.status       === 'fulfilled') this.locations   = norm(locs.value.data);
      });
    },

    clearFilters() {
      this.tableData.department_id = '';
      this.tableData.unit_id       = '';
      this.tableData.position_id   = '';
      this.tableData.location_id   = '';
      this.fetchEmployees();
    },

    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
      this.exportOpen         = false;
    },

    getEmpValue(emp, key) {
      switch (key) {
        case 'employee_no': return emp.employee_no || '—';
        case 'name':        return (`${emp.first_name || ''} ${emp.last_name || ''}`).trim() || '—';
        case 'email':       return emp.email || '—';
        case 'phone':       return emp.phone_number || '—';
        case 'department':  return emp.department?.name || '—';
        case 'unit':        return emp.unit?.name || '—';
        case 'position':    return emp.position?.name || '—';
        case 'paygrade':    return emp.paygrade?.name || '—';
        case 'location':    return emp.location?.name || '—';
        case 'hire_date':   return emp.hire_date || '—';
        case 'status':      return emp.is_active ? 'Active' : 'Inactive';
        default:            return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { ...this.tableData, page: 1, length: 10000 };
        const res    = await axios.get('/payroll/employees', { params });
        const rows   = Array.isArray(res.data?.data?.data)
          ? res.data.data.data
          : Array.isArray(res.data?.data) ? res.data.data : [];

        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(emp => cols.map(c => this.getEmpValue(emp, c.key)));
        const date    = new Date().toISOString().slice(0, 10);

        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body]
            .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `employees-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });

          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(26, 43, 69);
          doc.text('Employees Directory', 40, 46);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);

          doc.autoTable({
            startY: 78,
            head: [headers],
            body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles,
            tableWidth: usable,
            margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8);
              doc.setTextColor(192, 202, 214);
              doc.text(
                `Page ${data.pageNumber}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 16,
                { align: 'center' }
              );
            },
          });
          doc.save(`employees-${date}.pdf`);
        }
      } catch {
        this.toast.error('Export failed. Please try again.');
      } finally {
        this.exportLoading = false;
      }
    },

    selectAll() {
      this.selectedModel = this.allSelected ? this.employees.filter(d => !d.is_super).map(d => d.id) : [];
    },

    mapEmployee(emp) {
      return {
        id:         emp?.id,
        firstName:  emp?.first_name  || '',
        lastName:   emp?.last_name   || '',
        email:      emp?.email       || '',
        phone:      emp?.phone       || '—',
        dob:        emp?.dob         || null,
        department:
          emp?.employement?.department?.name ||
          emp?.employment?.department?.name  ||
          emp?.department?.name              ||
          emp?.department                    || '—',
      };
    },

    getInitials(first, last) {
      const a = first?.trim()?.charAt(0) || '';
      const b = last?.trim()?.charAt(0)  || '';
      return (a + b).toUpperCase() || '?';
    },

    formatBirthday(dob) {
      if (!dob) return '—';
      return new Date(dob).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    },

    flatError(msg) {
      if (typeof msg === 'string') return msg;
      if (Array.isArray(msg)) return msg[0];
      if (typeof msg === 'object') return Object.values(msg).flat()[0] ?? 'Validation error.';
      return 'An error occurred.';
    },

    closeDropdown() { this.dropdownOpen = false; },

    handleNew(type) {
      this.dropdownOpen = false;
      if (type === 'single') {
        this.$router.push('/create-employee');
      } else if (type === 'invite') {
        this.inviteForm = { first_name: '', last_name: '', email: '', personal_email: '' };
        this.inviteOpen = true;
      } else if (type === 'bulk_invite') {
        this.bulkFile = null;
        this.bulkInviteOpen = true;
      } else if (type === 'upload') {
        this.uploadFile       = null;
        this.uploadDepartment = '';
        this.uploadOpen       = true;
      }
    },

    debouncedFetch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.fetchEmployees(), 300);
    },

    submitInvite() {
      this.crudLoading = true;
      axios.post('/payroll/employees-invite', this.inviteForm)
        .then(({ data }) => {
          this.toast.success(data.message || 'Invitation sent successfully.');
          this.inviteOpen = false;
          this.$router.push('/manage-employees-invites');
        })
        .catch(err => {
          const status = err.response?.status;
          if (status === 422) this.toast.warning(this.flatError(err.response.data.message));
          else this.toast.error(err.response?.data?.message ?? 'Could not send invitation.');
        })
        .finally(() => { this.crudLoading = false; });
    },

    // -- Bulk invite template -------------------------------------------
    downloadTemplate() {
      const headers  = ['first_name', 'last_name', 'official_email', 'personal_email'];
      const examples = [
        ['John',   'Doe',     'john.doe@company.com',       'john.personal@gmail.com'],
        ['Jane',   'Smith',   'jane.smith@company.com',     'jane.smith@yahoo.com'],
        ['Samuel', 'Johnson', 'samuel.johnson@company.com', ''],
      ];
      const csvContent = [headers.join(','), ...examples.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = 'bulk_invite_template.csv';
      link.click();
      URL.revokeObjectURL(url);
    },

    onBulkFileDrop(e) { this.bulkFile = e.dataTransfer.files[0] ?? null; },

    submitBulkInvite() {
      if (!this.bulkFile) return;
      this.crudLoading = true;
      const form = new FormData();
      form.append('file', this.bulkFile, this.bulkFile.name);
      axios.post('/payroll/employees-invite/bulk', form)
        .then(({ data }) => {
          this.toast.success(data.message || 'Bulk invites sent successfully.');
          this.bulkInviteOpen = false;
          this.bulkFile = null;
          this.$router.push('/manage-employees-invites');
        })
        .catch(err => {
          const status = err.response?.status;
          if (status === 422) this.toast.warning(this.flatError(err.response.data.message));
          else this.toast.error(err.response?.data?.message ?? 'Bulk invite failed.');
        })
        .finally(() => { this.crudLoading = false; });
    },

    // -- Upload employees template --------------------------------------
    downloadUploadTemplate() {
      const headers  = [
        'employee_no', 'first_name', 'last_name', 'middle_name', 'official_email', 'personal_email', 'phone_number',
        'hire_date', 'dob', 'gender', 'address', 'post_code', 'emergency_contact_name', 'emergency_contact_email', 
        'emergency_contact_phone', 'emergency_contact_address', 'emergency_contact_relationship', 
        'bank_account_name', 'bank_account_no', 'bank_sortcode', 'tax_number','status'
      ];
      // const examples = [
      //   ['DCA-00123','John',   'Doe',     '',     'john.doe@company.com', '',      '+234 800 000 0001', '2024-01-15', '1990-05-21', 'Male',   '12 Main Street Lagos',    'LA1 2AB', '', '','','','','','','','','active'],
      //   ['DCA-00124','Jane',   'Smith',   'Mary', 'jane.smith@company.com', '',    '+234 800 000 0002', '2024-02-01', '1988-11-03', 'Female', '45 Park Avenue Abuja',    'AB3 4CD', '',     '','','','','','','','','acitve'],
      //   ['DCA-00125','Samuel', 'Johnson', '',     'samuel.johnson@company.com', '', '+234 800 000 0003', '2024-03-10', '1995-07-14', 'Male',   '7 Victoria Island Lagos', 'VI5 6EF', '',          '','','','','','','','','suspended'],
      // ];
      const examples = [
        ['DCA-00123','John',   'Doe',     '',     'essypng@gmail.com', '',      '+234 800 000 0001', '2024-01-15', '1990-05-21', 'Male',   '12 Main Street Lagos',    'LA1 2AB', '', '','','','','','','','','active'],
        ['DCA-00124','Jane',   'Smith',   'Mary', 'info@essyp.com', '',    '+234 800 000 0002', '2024-02-01', '1988-11-03', 'Female', '45 Park Avenue Abuja',    'AB3 4CD', '',     '','','','','','','','','on-leave'],
        ['DCA-00125','Samuel', 'Johnson', '',     'fmogbana@outlook.com', '', '+234 800 000 0003', '2024-03-10', '1995-07-14', 'Male',   '7 Victoria Island Lagos', 'VI5 6EF', '',          '','','','','','','','','suspended'],
      ];
      const csvContent = [headers.join(','), ...examples.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = 'upload_employees_template.csv';
      link.click();
      URL.revokeObjectURL(url);
    },

    onUploadFileDrop(e) { this.uploadFile = e.dataTransfer.files[0] ?? null; },

    submitUpload() {
      if (!this.uploadFile) return;
      this.crudLoading = true;
      const form = new FormData();
      form.append('file', this.uploadFile, this.uploadFile.name);
      if (this.uploadDepartment) form.append('department', this.uploadDepartment);
      axios.post('/payroll/employees/import', form)
        .then(({ data }) => {
          this.toast.success(data.message || 'Employees uploaded successfully.');
          this.uploadOpen = false;
          this.uploadFile = null;
          this.fetchEmployees();
        })
        .catch(err => {
          const status = err.response?.status;
          if (status === 422) this.toast.warning(this.flatError(err.response.data.message));
          else this.toast.error(err.response?.data?.message ?? 'Upload failed.');
        })
        .finally(() => { this.crudLoading = false; });
    },

    async callBulkDeleteButton() {
      try {
        const { data } = await axios.post('/payroll/employees/delete-bulk', {
          employees: this.selectedModel,
        });
        this.toast.success(data.message);
        this.selectedModel = [];
        this.allSelected   = false;
        this.fetchEmployees();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) this.toast.error(this.flatError(err.response.data.message));
        else this.toast.error(err.response.data?.message ?? 'Bulk delete failed.');
      }
    },
  },
};
</script>


<style scoped>
/* -- Page ---------------------------------------------------------------- */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* -- Search -------------------------------------------------------------- */
.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* -- Buttons ------------------------------------------------------------- */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.45; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-ghost:hover { background:#e4eaf2; }
.btn-danger-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; position:relative; font-family:inherit; }
.btn-danger-outline:hover:not(:disabled) { background:#fff5f5; }
.btn-danger-outline:disabled { opacity:.45; cursor:not-allowed; }
.chevron { font-size:11px; }
.me-1 { margin-right:4px; }
.mt-3 { margin-top:12px; }
.bulk-count { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; font-size:10px; font-weight:700; border-radius:50%; width:17px; height:17px; margin-left:2px; }

/* -- Dropdown ------------------------------------------------------------ */
.dropdown-wrap   { position:relative; }
.action-dropdown { position:absolute; right:0; top:calc(100% + 6px); z-index:200; background:#fff; border:1px solid #e4eaf2; border-radius:12px; box-shadow:0 12px 32px rgba(0,0,0,.12); min-width:240px; padding:6px; }
.action-dropdown-item { display:flex; align-items:center; gap:12px; width:100%; padding:10px 12px; border:none; background:transparent; border-radius:8px; cursor:pointer; text-align:left; transition:background .12s; font-family:inherit; }
.action-dropdown-item:hover { background:#f4f7fb; }
.action-dropdown-icon { width:32px; height:32px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
.action-dropdown-label { font-size:13px; font-weight:600; color:#1a2b45; margin-bottom:2px; }
.action-dropdown-hint  { font-size:11.5px; color:#7a90b0; }

/* -- Filter button ------------------------------------------------------- */
.btn-filter { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; transition:all .15s; white-space:nowrap; font-family:inherit; position:relative; }
.btn-filter:hover { background:#e4eaf2; border-color:#c8d6e8; }
.btn-filter--active { background:#eef4ff; color:#004AAD; border-color:#c5d9f7; }
.filter-count { display:inline-flex; align-items:center; justify-content:center; background:#004AAD; color:#fff; font-size:10px; font-weight:700; border-radius:50%; width:17px; height:17px; margin-left:2px; }

/* -- Export button ------------------------------------------------------- */
.btn-export { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; transition:all .15s; white-space:nowrap; font-family:inherit; }
.btn-export:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.btn-export:disabled { opacity:.45; cursor:not-allowed; }

/* -- Filter panel -------------------------------------------------------- */
.filter-panel { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px 20px; margin-bottom:16px; }
.filter-panel-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
.filter-field { display:flex; flex-direction:column; gap:5px; }
.filter-label { font-size:12px; font-weight:600; color:#5a7091; }
.filter-select--full { width:100%; }
.filter-panel-footer { margin-top:12px; padding-top:12px; border-top:1px solid #f0f4f9; }
.btn-clear-filters { display:inline-flex; align-items:center; gap:6px; background:transparent; color:#e03535; font-size:13px; font-weight:600; padding:6px 12px; border-radius:7px; border:1px solid #fecaca; cursor:pointer; transition:all .15s; font-family:inherit; }
.btn-clear-filters:hover { background:#fff5f5; }

/* -- Filter panel slide transition --------------------------------------- */
.filter-slide-enter-active, .filter-slide-leave-active { transition:all .2s ease; }
.filter-slide-enter, .filter-slide-leave-to { opacity:0; transform:translateY(-8px); }

/* -- Stat cards ---------------------------------------------------------- */
.stat-cards { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; margin-bottom:20px; }
.stat-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.stat-icon  { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.stat-value { font-size:26px; font-weight:800; color:#1a2b45; line-height:1; margin-bottom:4px; }
.stat-label { font-size:13px; color:#7a90b0; }

/* -- Data card ----------------------------------------------------------- */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* -- States -------------------------------------------------------------- */
.state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
.state-icon--red  { background:#fff0f0; color:#e03535; }
.state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub        { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell  { color:#4a5e78; font-size:13px; }

/* -- Table toolbar ------------------------------------------------------- */
.table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; }
.table-count   { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.selected-hint { color:#004AAD; }
.per-page-wrap  { display:flex; align-items:center; gap:7px; }
.per-page-label { font-size:13px; color:#7a90b0; }
.per-page-select { padding:5px 10px; border:1px solid #dde5f0; border-radius:7px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
.per-page-select:focus { border-color:#004AAD; }

/* -- Pagination ---------------------------------------------------------- */
.pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.pagination-info     { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; gap:3px; }
.pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
.pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
.pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }

/* -- Table --------------------------------------------------------------- */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody tr.row--selected { background:#edf4ff; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.col-check   { width:40px; }
.col-actions { width:56px; text-align:center; }
.row-clickable { cursor:pointer; }

/* -- Employee cell ------------------------------------------------------- */
.emp-cell   { display:flex; align-items:center; gap:10px; }
.emp-avatar { width:34px; height:34px; border-radius:50%; background:#004AAD; color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.emp-avatar--img { object-fit:cover; background:transparent; }
.emp-name   { font-size:13px; font-weight:600; color:#1a2b45; line-height:1.3; }
.emp-email  { font-size:11.5px; color:#7a90b0; margin-top:2px; }
.owner-badge { display:inline-flex; align-items:center; gap:4px; background:#fefce8; color:#a16207; border:1px solid #fde68a; border-radius:20px; font-size:10.5px; font-weight:700; padding:1px 8px; margin:3px 0 2px; white-space:nowrap; line-height:1.6; }
.owner-badge-icon { font-size:9px; }

/* -- Department badge ---------------------------------------------------- */
.dept-badge { display:inline-block; background:rgba(0,74,173,.08); color:#004AAD; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }

/* -- Action icon button -------------------------------------------------- */
.action-icon-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; margin:0 auto; }
.action-icon-btn:hover { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }

/* -- Modals -------------------------------------------------------------- */
.modal-overlay      { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:480px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; max-height:90vh; display:flex; flex-direction:column; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-hd   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:flex-start; gap:12px; flex-shrink:0; }
.modal-hd-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.modal-ttl  { font-size:15px; font-weight:700; color:#1a2b45; display:block; margin-bottom:2px; }
.modal-sub  { font-size:12px; color:#7a90b0; margin:0; }
.modal-close-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; margin-left:auto; flex-shrink:0; transition:background .15s; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-bd   { padding:20px 24px; display:flex; flex-direction:column; gap:14px; overflow-y:auto; flex:1; }
.modal-ft   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; }

/* -- Form fields --------------------------------------------------------- */
.field-group  { display:flex; flex-direction:column; gap:5px; }
.field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
.req          { color:#e03535; }
.field-input  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-select { appearance:auto; cursor:pointer; }

/* -- Upload zone --------------------------------------------------------- */
.upload-zone { border:2px dashed #dde5f0; border-radius:10px; padding:32px 20px; text-align:center; cursor:pointer; transition:all .15s; }
.upload-zone:hover, .upload-zone--active { border-color:#004AAD; background:rgba(0,74,173,.03); }
.upload-icon  { font-size:28px; color:#7a90b0; margin-bottom:10px; }
.upload-label { font-size:13.5px; font-weight:600; color:#1a2b45; margin:0 0 4px; }
.upload-hint  { font-size:12px; color:#7a90b0; margin:0; }

/* -- Template hint ------------------------------------------------------- */
.template-hint { display:flex; align-items:center; gap:8px; font-size:12.5px; color:#7a90b0; padding:10px 14px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; }
.template-hint-icon { color:#7a90b0; font-size:13px; flex-shrink:0; }
.btn-download-template { display:inline-flex; align-items:center; gap:5px; background:none; border:none; color:#004AAD; font-size:12.5px; font-weight:600; cursor:pointer; padding:0; font-family:inherit; text-decoration:underline; text-underline-offset:2px; margin-left:auto; white-space:nowrap; }
.btn-download-template:hover { color:#003b8e; }

/* -- Column reference ---------------------------------------------------- */
.template-cols       { padding:12px 14px; background:#f0f4fb; border:1px solid #dde5f0; border-radius:8px; }
.template-cols-title { font-size:12px; font-weight:600; color:#1a2b45; margin:0 0 8px; }
.template-col-list   { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:6px; }
.col-tag             { font-size:11.5px; font-weight:600; padding:3px 10px; border-radius:20px; font-family:monospace; }
.col-tag--required   { background:rgba(0,74,173,.1); color:#004AAD; border:1px solid rgba(0,74,173,.2); }
.col-tag--optional   { background:#f4f7fb; color:#64748b; border:1px solid #dde5f0; }
.col-req             { color:#e03535; }
.template-cols-note  { font-size:11px; color:#7a90b0; margin:0; }

/* -- Responsive ---------------------------------------------------------- */
@media (max-width:768px) {
  .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  .search-input { width:100%; }
  .stat-cards   { grid-template-columns:1fr; }
  .action-dropdown { right:auto; left:0; }
}

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>